import {
  Box,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Switch,
  Text,
  type BoxProps,
} from '@chakra-ui/react';
import { FiCheck, FiChevronDown } from 'react-icons/fi';

import {
  PLAN_REVAMP_RADIUS_SM,
  PLAN_TEXT,
  controlHeight,
} from 'pages/PlanRevamp/PlanPageUIRevamp/planPageRevampUi';
import { font, fontSize, shadow } from 'styles/revampTokens';

import colors from '../dataTheme';

import type { ReactNode } from 'react';

/** Title + optional helper line that opens every wizard step. */
export const StepHeading = ({ title, subtitle }: { title: string; subtitle?: string }) => (
  <Box mb={4}>
    <Text {...PLAN_TEXT.bodySemibold} color={colors.textDark}>
      {title}
    </Text>
    {subtitle && (
      <Text {...PLAN_TEXT.labelSm} color={colors.textSecondary} mt='2px'>
        {subtitle}
      </Text>
    )}
  </Box>
);

/** Small label that sits above an input. */
export const FieldLabel = ({ children, ...rest }: { children: ReactNode } & BoxProps) => (
  <Text {...PLAN_TEXT.labelSmMedium} color={colors.textSecondary} mb='6px' {...rest}>
    {children}
  </Text>
);

/** Input styling shared by the wizard's text fields (same look as the edit modals). */
export const stepInputStyle = {
  h: controlHeight,
  px: '10px',
  borderRadius: PLAN_REVAMP_RADIUS_SM,
  borderColor: colors.border,
  bg: colors.surface,
  ...PLAN_TEXT.body,
  fontSize: fontSize.body,
  color: colors.textDark,
  _placeholder: { color: colors.textSecondary },
  _hover: { borderColor: colors.border },
  _focusVisible: { borderColor: colors.primary, boxShadow: 'none' },
} as const;

/** A selectable option row (radio-card) used for single-choice steps. */
export const optionCardStyle = (isSelected: boolean) =>
  ({
    display: 'flex',
    alignItems: 'center',
    gap: 3,
    w: '100%',
    px: 3,
    py: '10px',
    cursor: 'pointer',
    textAlign: 'left',
    border: '1px solid',
    borderColor: isSelected ? colors.primary : colors.border,
    borderRadius: PLAN_REVAMP_RADIUS_SM,
    bg: isSelected ? colors.activeBg : colors.surface,
    transition: 'background 0.12s ease, border-color 0.12s ease',
    _hover: { bg: isSelected ? colors.activeBg : colors.hover },
  } as const);

/** Radio dot drawn by hand so it matches the circles used across the Data screens. */
export const RadioDot = ({ isSelected }: { isSelected: boolean }) => (
  <Box
    w='14px'
    h='14px'
    flexShrink={0}
    borderRadius='full'
    border='1px solid'
    borderColor={isSelected ? colors.primary : colors.border}
    display='flex'
    alignItems='center'
    justifyContent='center'
  >
    {isSelected && <Box w='6px' h='6px' borderRadius='full' bg={colors.primary} />}
  </Box>
);

export interface StepSelectOption {
  value: string | number;
  label: string;
}

interface StepSelectProperties {
  value?: string | number | null;
  options: StepSelectOption[];
  placeholder?: string;
  /** Adds an empty "placeholder" row at the top that clears the value. */
  isClearable?: boolean;
  isInvalid?: boolean;
  isDisabled?: boolean;
  onChange: (value: string | number) => void;
}

/** Single-select dropdown in the same look as the landing-page filters. */
export const StepSelect = ({
  value,
  options,
  placeholder = 'Select',
  isClearable = false,
  isInvalid = false,
  isDisabled = false,
  onChange,
}: StepSelectProperties) => {
  const selected = options.find((option) => String(option.value) === String(value ?? ''));
  return (
    <Menu matchWidth isLazy>
      <MenuButton
        type='button'
        w='100%'
        h={controlHeight}
        px='10px'
        display='flex'
        alignItems='center'
        border='1px solid'
        borderColor={isInvalid ? colors.danger : colors.border}
        borderRadius={PLAN_REVAMP_RADIUS_SM}
        bg={colors.surface}
        {...PLAN_TEXT.body}
        fontSize={fontSize.body}
        color={selected ? colors.textDark : colors.textSecondary}
        opacity={isDisabled ? 0.5 : 1}
        pointerEvents={isDisabled ? 'none' : 'auto'}
        transition='background 0.12s ease, border-color 0.12s ease'
        _hover={{ bg: colors.hover }}
        _expanded={{ borderColor: colors.primary }}
        sx={{ '& > span': { display: 'flex', alignItems: 'center', width: '100%' } }}
      >
        <Text flex={1} textAlign='left' noOfLines={1}>
          {selected?.label ?? placeholder}
        </Text>
        <FiChevronDown size={14} color={colors.textSecondary} />
      </MenuButton>
      <MenuList
        p='4px'
        bg={colors.surface}
        border='1px solid'
        borderColor={colors.border}
        borderRadius={PLAN_REVAMP_RADIUS_SM}
        boxShadow={shadow.overlay}
        maxH='240px'
        overflowY='auto'
        className='scroll'
        zIndex={9999}
      >
        {[...(isClearable ? [{ value: '', label: placeholder }] : []), ...options].map((option) => {
          const isSelected = String(option.value) === String(value ?? '');
          return (
            <MenuItem
              key={String(option.value)}
              onClick={() => onChange(option.value)}
              borderRadius='6px'
              gap={2}
              px={2}
              py='7px'
              fontFamily={font.body}
              fontSize='13px'
              color={option.value === '' ? colors.textSecondary : colors.textDark}
              bg={isSelected ? colors.activeBg : colors.surface}
              _hover={{ bg: isSelected ? colors.activeBg : colors.hover }}
              _focus={{ bg: isSelected ? colors.activeBg : colors.hover }}
            >
              <Box w='14px' flexShrink={0} display='flex'>
                {isSelected && <FiCheck size={14} color={colors.primary} />}
              </Box>
              {option.label}
            </MenuItem>
          );
        })}
      </MenuList>
    </Menu>
  );
};

/** Square checkbox with a label, drawn by hand to match the Data screens. */
export const StepCheckbox = ({
  isChecked,
  onChange,
  children,
  isDisabled = false,
}: {
  isChecked: boolean;
  onChange: (next: boolean) => void;
  children: ReactNode;
  isDisabled?: boolean;
}) => (
  <Flex
    as='button'
    type='button'
    role='checkbox'
    aria-checked={isChecked}
    alignItems='center'
    gap={2}
    textAlign='left'
    cursor={isDisabled ? 'not-allowed' : 'pointer'}
    opacity={isDisabled ? 0.5 : 1}
    onClick={() => !isDisabled && onChange(!isChecked)}
  >
    <Flex
      w='16px'
      h='16px'
      flexShrink={0}
      alignItems='center'
      justifyContent='center'
      borderRadius='4px'
      border='1px solid'
      borderColor={isChecked ? colors.primary : colors.border}
      bg={isChecked ? colors.primary : colors.surface}
      transition='background 0.12s ease, border-color 0.12s ease'
    >
      {isChecked && <FiCheck size={12} strokeWidth={3} color={colors.white} />}
    </Flex>
    <Text {...PLAN_TEXT.body} fontSize={fontSize.body} color={colors.textDark}>
      {children}
    </Text>
  </Flex>
);

/** Toggle whose "on" colour is the accent purple instead of Chakra's default blue. */
export const StepSwitch = (properties: React.ComponentProps<typeof Switch>) => (
  <Switch
    {...properties}
    sx={{
      '.chakra-switch__track': { bg: colors.border },
      '.chakra-switch__track[data-checked]': { bg: colors.primary },
      '.chakra-switch__track:focus, .chakra-switch__track[data-focus]': { boxShadow: 'none' },
    }}
  />
);

/** Formik `<ErrorMessage component={FieldError} />` renderer. */
export const FieldError = ({ children }: { children?: ReactNode }) => (
  <Text {...PLAN_TEXT.labelSm} color={colors.danger} mt='4px'>
    {children}
  </Text>
);

/** Right-aligned row that holds the Back / Continue buttons at the end of a step. */
export const StepFooterRow = ({ children }: { children: ReactNode }) => (
  <Flex justifyContent='flex-end' pt={6}>
    {children}
  </Flex>
);

/** Radio option with a label, for compact inline choices (no card border). */
export const StepRadio = ({
  isSelected,
  onSelect,
  children,
}: {
  isSelected: boolean;
  onSelect: () => void;
  children: ReactNode;
}) => (
  <Flex
    as='button'
    type='button'
    role='radio'
    aria-checked={isSelected}
    alignItems='center'
    gap={2}
    textAlign='left'
    cursor='pointer'
    onClick={onSelect}
  >
    <RadioDot isSelected={isSelected} />
    <Text {...PLAN_TEXT.body} fontSize={fontSize.body} color={colors.textDark}>
      {children}
    </Text>
  </Flex>
);

/** Inline helper/warning line shown inside a step. */
export const StepNotice = ({
  tone = 'muted',
  children,
  ...rest
}: { tone?: 'muted' | 'warning' | 'danger'; children: ReactNode } & BoxProps) => (
  <Text
    {...PLAN_TEXT.labelSm}
    color={
      tone === 'warning' ? colors.warning : tone === 'danger' ? colors.danger : colors.textSecondary
    }
    {...rest}
  >
    {children}
  </Text>
);

/** Shared sizing for the wizard's Previous / Continue buttons. */
export const stepFooterButton = {
  h: controlHeight,
  px: 4,
  minW: '84px',
  borderRadius: PLAN_REVAMP_RADIUS_SM,
  ...PLAN_TEXT.toolbarTab,
  textTransform: 'none',
  width: 'fit-content',
  boxShadow: 'none',
} as const;
